import type { AppCode, Permission } from './permissions';

/**
 * O catálogo dos produtos da Yard.
 *
 * POR QUE ISTO EXISTE
 *
 *   A tela de Apps trazia a lista cravada no corpo dela: `['MKT','CRM','FLW']`.
 *   Bio e Pop-up já existiam havia meses e nunca apareceram ali; o Portal
 *   entrou agora e também não apareceria. Quer dizer: existia produto vendido,
 *   com tabela, rota e permissão no lugar, que NÃO TINHA COMO SER LIGADO por
 *   ninguém - nem pela Yard, nem pelo cliente.
 *
 *   Uma lista só, aqui, e as telas leem dela. Produto novo entra em um lugar.
 *
 * A FAMÍLIA DE COR NÃO É DECORAÇÃO
 *
 *   O contrato do design system (seção 3) amarra cor a função, e não a gosto:
 *   amarelo é plataforma, roxo é demanda, verde é vitrine, ciano é conversa.
 *   É o que faz alguém reconhecer de longe se está numa tela que fala com o
 *   público ou numa que fala com a equipe.
 */

export type Familia = 'plataforma' | 'demanda' | 'vitrine' | 'conversa';

export type Produto = {
  code: AppCode;
  /** O nome da marca, como o design system escreve. Caixa baixa, sem ponto. */
  marca: string;
  /** A palavra que vai depois do prefixo no lockup ("crm", "marketing",
   *  "flow"...). Vazia quando o produto é a própria assinatura (o Suite) ou
   *  quando a marca inteira é o prefixo (myard.bio, marcada `mesclada`). */
  sufixo: string;
  /** A palavra ANTES do sufixo, em tinta neutra. Quase sempre "yard" - a
   *  exceção é o pop-up flow, que mora na casa do cliente e por isso não leva
   *  a assinatura da Yard na frente (marca.html, 02.2). */
  prefixo?: string;
  /** myard.bio: não é "yard" + sufixo, é uma palavra só, endereço, com o "m"
   *  e o ".bio" na cor da família e o "yard" no meio em tinta neutra
   *  (marca.html, 02.2, "a exceção myard.bio"). Só esse produto usa isto. */
  mesclada?: boolean;
  familia: Familia;
  /** Uma frase. O que o produto resolve, não o que ele tem. */
  resumo: string;
  /** Onde ele abre no painel. */
  rota: string;
  /** Outras rotas que pertencem ao produto sem morar sob `rota` (ex.: a
   *  biblioteca de e-mails em `/emails`, que é Marketing). Servem para a
   *  família de cor, o selo e o item ativo do menu. */
  rotasExtras?: string[];
  /** A permissão mínima para ver a primeira tela dele. */
  entrada: Permission;
};

export const PRODUTOS: Produto[] = [
  {
    code: 'MKT',
    marca: 'yard marketing',
    sufixo: 'marketing',
    familia: 'demanda',
    resumo: 'Atrai quem ainda não conhece: página, formulário, e-mail e automação.',
    rota: '/mkt',
    rotasExtras: ['/emails'],
    entrada: 'mkt.contact:read',
  },
  {
    code: 'CRM',
    marca: 'yard crm',
    sufixo: 'crm',
    familia: 'plataforma',
    resumo: 'O funil e o que acontece dentro dele, da primeira conversa ao fechamento.',
    rota: '/crm',
    entrada: 'crm.deal:read',
  },
  {
    code: 'FLW',
    marca: 'yard flow',
    sufixo: 'flow',
    familia: 'conversa',
    resumo: 'WhatsApp e chat numa caixa só, com atendimento humano e robô.',
    rota: '/flw',
    entrada: 'flw.conversation:handle',
  },
  {
    code: 'POP',
    marca: 'pop-up flow',
    sufixo: 'flow',
    prefixo: 'pop-up',
    familia: 'conversa',
    resumo: 'A conversa que abre sozinha no site e transforma visita em contato.',
    rota: '/pop',
    entrada: 'pop.report:read',
  },
  {
    code: 'BIO',
    marca: 'myard.bio',
    sufixo: '',
    mesclada: true,
    familia: 'vitrine',
    resumo: 'A vitrine pública da empresa: links, catálogo, quiz e agendamento.',
    rota: '/bio',
    entrada: 'bio.site:read',
  },
  {
    code: 'MAIL',
    marca: 'yard suitemail',
    sufixo: 'suitemail',
    // Preto e azul: grafite com o ciano do Meet, como o Mail sempre foi
    // (pedido de 17/10/2026, depois do redesenho que o deixou amarelo).
    familia: 'conversa',
    resumo: 'E-mail, documentos, agenda e reunião na marca da empresa, sem licença por usuário.',
    rota: '/mail',
    entrada: 'mail.message:read',
  },
];

export const produtoDe = (code: AppCode): Produto | undefined =>
  PRODUTOS.find((p) => p.code === code);


/** O token de cor da família. Uma peça carrega uma família só (contrato, 3). */
export const COR_DA_FAMILIA: Record<Familia, string> = {
  plataforma: 'var(--yd-amarelo-100)',
  demanda: 'var(--yd-roxo-500)',
  vitrine: 'var(--yd-verde-100)',
  conversa: 'var(--yd-ciano-400)',
};

/**
 * A tinta que se lê sobre a cor da família.
 *
 * Roxo 500 é escuro e pede branco; amarelo, verde e ciano são claros e pedem
 * grafite. Escrever branco sobre o amarelo da marca dá 1,1:1 - é o erro que a
 * seção 3 do contrato chama de "cor de marca não é cor de texto".
 */
export const TINTA_DA_FAMILIA: Record<Familia, string> = {
  plataforma: 'var(--yd-grafite)',
  demanda: 'var(--yd-branco)',
  vitrine: 'var(--yd-grafite)',
  conversa: 'var(--yd-grafite)',
};

/** A porta do Flow precisa abrir uma tela que o atendente possa usar. */
export function destinoInicialDoFlow(permissoes: readonly Permission[]): '/flw/dashboard' | '/flw/inbox' {
  return permissoes.includes('flw.queue:supervise') ? '/flw/dashboard' : '/flw/inbox';
}
